import { cn } from "@gotts.ai/ui";
import { AlertTriangle, Send, X } from "lucide-react";

import { Address } from "../../components/Address";

import type { AbiFunction } from "./types";

interface SendConfirmationProps {
  fn: AbiFunction;
  params: Record<string, string>;
  ethValue: string;
  gasEstimate: bigint | null;
  walletAddress: string;
  onConfirm: () => void;
  onCancel: () => void;
  loading: boolean;
}

export function SendConfirmation({
  fn,
  params,
  ethValue,
  gasEstimate,
  walletAddress,
  onConfirm,
  onCancel,
  loading,
}: SendConfirmationProps) {
  return (
    <div className="space-y-2 border-l-2 border-l-amber-500/40 bg-[rgba(245,158,11,0.04)] p-3">
      <div className="flex items-center gap-2">
        <AlertTriangle size={12} className="text-amber-400" />
        <span className="text-xs font-semibold text-amber-400">
          Confirm Transaction
        </span>
      </div>

      <div className="space-y-1 text-[10px]">
        <div className="text-[var(--color-text-muted)]">
          Function:{" "}
          <span className="font-mono text-[var(--color-text)]">{fn.name}</span>
        </div>

        {fn.inputs.length > 0 && (
          <div className="text-[var(--color-text-muted)]">
            Parameters:
            {fn.inputs.map((input) => (
              <div
                key={input.name}
                className="ml-2 font-mono text-[var(--color-text-dim)]"
              >
                {input.name}: {params[input.name] ?? "(empty)"}
              </div>
            ))}
          </div>
        )}

        {ethValue && parseFloat(ethValue) > 0 && (
          <div className="text-[var(--color-text-muted)]">
            ETH Value:{" "}
            <span className="font-mono text-[var(--color-text)]">
              {ethValue} ETH
            </span>
          </div>
        )}

        {gasEstimate !== null && (
          <div className="text-[var(--color-text-muted)]">
            Gas estimate:{" "}
            <span className="font-mono text-[var(--color-text-dim)]">
              {gasEstimate.toLocaleString()}
            </span>
          </div>
        )}

        <div className="text-[var(--color-text-muted)]">
          Sending as:{" "}
          <Address
            address={walletAddress as `0x${string}`}
            fontSize={10}
            leadChars={6}
            tailChars={4}
          />
        </div>
      </div>

      <div className="flex items-center gap-2 pt-1">
        <button
          type="button"
          onClick={onConfirm}
          disabled={loading}
          className={cn(
            "octa-sm flex items-center gap-1.5 px-4 py-2 font-mono text-xs font-medium transition-all duration-200",
            "bg-[rgba(201,168,76,0.2)] text-[var(--color-gold)] hover:bg-[rgba(201,168,76,0.3)] active:scale-[0.97]",
            "disabled:opacity-40",
          )}
        >
          <Send size={10} />
          {loading ? "Sending..." : "Confirm"}
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={loading}
          className={cn(
            "octa-sm flex items-center gap-1.5 px-3 py-2 text-xs text-[var(--color-text-muted)] transition-all",
            "bg-[rgba(255,255,255,0.03)] hover:bg-[rgba(255,255,255,0.06)]",
            "disabled:opacity-40",
          )}
        >
          <X size={10} />
          Cancel
        </button>
      </div>
    </div>
  );
}
